/**
 * AS Fashion Home page — asfashion.store reference layout.
 *
 * Renders ONLY when the tenant's active preset is `asfashion`. All other
 * presets keep using the legacy Home composition in `Home.jsx`.
 *
 * Reference structure (asfashion.store):
 *   1. Hero carousel  → shared <AsFashionHero />
 *   2. Circular category "story chips"  → shared <AsFashionCategoryGrid />
 *   3. Product sections (New / Trending / Best of Sale) — each has:
 *        - centred thin-serif H2
 *        - muted uppercase caption
 *        - 40px hairline divider
 *        - 2/3/4-col grid of product cards
 *        - "View All" ghost outline button below
 *   4. Featured product callout — split image + text
 *   5. Trust badges strip (Free Shipping · Premium Quality · Secure Payment)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { api, imgUrl, money } from "../../lib/api";
import { resolveTrustBadges } from "../../lib/trustBadges";
import {
  useBranding, resolveBrandImage, resolveAppDisplayName,
} from "../../context/BrandingContext";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { AsFashionHero, AsFashionCategoryGrid } from "./home/AsFashionHero";

// Validated portrait ethnic-fashion still-life. Confirmed reachable
// (200 OK) — the previous fallback was 404 which caused broken
// alt-text to render inside the Featured callout image slot.
// No hardcoded fallback image. A stock photo is never tenant-specific: the
// ethnic-wear still-life that used to live here rendered on every storefront
// on this preset, including ones selling groceries. When a tenant has not
// configured a Featured image we fall back to their OWN logo, and if they
// have none we render the panel without an image rather than borrowing
// someone else's marketing.

/* ---------- Product card (AS Fashion look) ------------------------------ */
function AsFashionProductCard({ product }) {
  const { toggleWishlist, inWishlist } = useCart();
  const { user } = useAuth();
  const liked = inWishlist(product.id);

  const onLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Sign in to save to wishlist");
      return;
    }
    try {
      const added = await toggleWishlist(product.id);
      toast.success(added ? "Added to wishlist" : "Removed from wishlist");
    } catch {
      toast.error("Could not update wishlist");
    }
  };

  const mrp = Number(product.mrp) || 0;
  const price = Number(product.price) || 0;
  const pct = mrp > price && mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;

  return (
    <Link
      to={`/product/${product.slug}`}
      className="group block h-full"
      data-testid={`asf-product-${product.slug}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-50 border border-gray-100 transition-all duration-300 lg:group-hover:shadow-md">
        {product.images?.[0] && (
          <img
            src={imgUrl(product.images[0])}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-500 ease-out lg:group-hover:scale-105"
            loading="lazy"
          />
        )}
        <button
          type="button"
          onClick={onLike}
          aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute top-2 right-2 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-sm transition-all duration-300 bg-white ${
            liked
              ? "text-[hsl(var(--kb-accent))]"
              : "text-gray-900 hover:text-[hsl(var(--kb-accent))] hover:bg-gray-100"
          }`}
          data-testid={`asf-wishlist-${product.slug}`}
        >
          <Heart
            size={18}
            strokeWidth={1.8}
            fill={liked ? "currentColor" : "none"}
          />
        </button>
      </div>
      <div className="space-y-1.5 px-1 mt-3 text-center flex flex-col items-center">
        <h3 className="text-[11px] sm:text-xs font-medium text-gray-800 leading-snug line-clamp-2 group-hover:text-[hsl(var(--kb-accent))] transition-colors duration-200">
          {product.title}
        </h3>
        <div className="flex flex-wrap items-center justify-center gap-1.5 mt-1">
          <span className="text-xs sm:text-sm font-bold text-gray-900">
            {money(price)}
          </span>
          {pct > 0 && (
            <>
              <span className="text-[10px] sm:text-xs text-gray-400 line-through font-medium">
                {money(mrp)}
              </span>
              <span className="text-[9px] sm:text-[10px] font-bold text-[hsl(var(--kb-accent))] bg-pink-50 border border-pink-100 px-1 py-0.5 tracking-tight">
                {pct}% OFF
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ---------- Product section (asfashion.store category block) ----------- */
function ProductSection({ title, subtitle, products, viewAllTo, testid }) {
  if (!products || products.length === 0) return null;
  return (
    <section
      className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8"
      data-testid={testid}
    >
      <div className="text-center mb-8">
        <h2 className="font-display text-2xl sm:text-3xl font-light tracking-wide text-[hsl(var(--kb-text))]">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs sm:text-sm text-[hsl(var(--kb-muted))] font-normal tracking-[0.15em] uppercase mt-2 max-w-2xl mx-auto">
            {subtitle}
          </p>
        )}
        <div className="w-10 h-px bg-[hsl(var(--kb-text))] mx-auto mt-3" />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
        {products.map((p) => (
          <AsFashionProductCard key={p.id} product={p} />
        ))}
      </div>
      {viewAllTo && (
        <div className="mt-8 flex justify-center">
          <Link
            to={viewAllTo}
            className="inline-block border border-[hsl(var(--kb-text))] text-[hsl(var(--kb-text))] hover:bg-[hsl(var(--kb-text))] hover:text-white px-8 py-3 text-sm font-medium uppercase tracking-wider transition-all duration-300"
            data-testid={`${testid}-view-all`}
          >
            View All
          </Link>
        </div>
      )}
    </section>
  );
}

/* ---------- Featured split callout -------------------------------------- */
function FeaturedCallout({ featured, imgIsLogo }) {
  // If the image 404s we drop the slot entirely. Swapping in a stock photo
  // is what put unrelated marketing on tenant storefronts in the first place.
  const [imgFailed, setImgFailed] = useState(false);
  if (!featured?.title) return null;
  return (
    <section
      className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-4"
      data-testid="asf-featured"
    >
      {/* Reference has no outer border — just a cream-tinted left panel
          flush with a full-bleed image on the right. */}
      <div className="grid grid-cols-1 md:grid-cols-2 overflow-hidden">
        <div className="flex items-center p-8 sm:p-12 order-2 md:order-1 bg-[hsl(var(--kb-surface-alt))]">
          <div className="space-y-4 text-left max-w-md">
            {featured.overline && (
              <span className="text-[hsl(var(--kb-muted))] text-xs font-medium uppercase tracking-[0.2em] block">
                {featured.overline}
              </span>
            )}
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-light text-[hsl(var(--kb-text))] leading-tight">
                {featured.title}
              </h2>
              {/* Hairline divider under the H2 matches the reference. */}
              <div className="w-10 h-px bg-[hsl(var(--kb-text))] mt-3" />
            </div>
            {featured.body && (
              <p className="text-sm text-[hsl(var(--kb-muted))] leading-relaxed">
                {featured.body}
              </p>
            )}
            {featured.cta_label && (
              <div className="pt-2">
                <Link
                  to={featured.cta_link || "/shop"}
                  className="border border-[hsl(var(--kb-text))] bg-[hsl(var(--kb-text))] hover:bg-white hover:text-[hsl(var(--kb-text))] text-white text-sm font-medium px-6 py-2.5 tracking-wider transition-all duration-300 inline-block uppercase"
                  data-testid="asf-featured-cta"
                >
                  {featured.cta_label}
                </Link>
              </div>
            )}
          </div>
        </div>
        {featured.image_url && !imgFailed && (
          <div
            className="relative overflow-hidden aspect-[4/5] md:aspect-[4/3] bg-[hsl(var(--kb-surface-alt))] group order-1 md:order-2"
            data-testid="asf-featured-image"
          >
            <img
              src={featured.image_url}
              alt={featured.title}
              className={
                imgIsLogo
                  ? "w-full h-full object-contain object-center p-10"
                  : "w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-700"
              }
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------- Trust badges strip ----------------------------------------- */
// One column per visible badge, so a hidden one never leaves an empty slot.
const TRUST_COLUMNS = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" };

// Content is the tenant's own, edited under Admin → Home Sections and
// delivered by /settings/app (`home_trust_badges`). Nothing is hardcoded
// here: `resolveTrustBadges` applies the defaults and drops hidden sections.
function TrustBadges({ badges }) {
  const items = resolveTrustBadges(badges, money);
  if (items.length === 0) return null;
  return (
    <section
      className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
      data-testid="asf-trust-badges"
    >
      <div
        className={`grid grid-cols-1 gap-6 ${TRUST_COLUMNS[items.length] || "sm:grid-cols-3"} border-t border-[hsl(var(--kb-border))] pt-10`}
      >
        {items.map(({ key, Icon, title, description }) => (
          <div
            key={key}
            className="flex flex-col items-center text-center p-6 space-y-2 min-w-0"
            data-testid={`asf-trust-badge-${key}`}
          >
            <Icon
              size={32}
              strokeWidth={1.5}
              className="text-[hsl(var(--kb-text))] shrink-0"
              aria-hidden="true"
            />
            <h3
              className="max-w-full text-sm font-semibold text-[hsl(var(--kb-text))] uppercase tracking-wider break-words [overflow-wrap:anywhere]"
              data-testid={`asf-trust-title-${key}`}
            >
              {title}
            </h3>
            <p
              className="max-w-full text-sm text-[hsl(var(--kb-muted))] break-words [overflow-wrap:anywhere]"
              data-testid={`asf-trust-description-${key}`}
            >
              {description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Page component --------------------------------------------- */
export default function AsFashionHome() {
  const { branding, loading: brandingLoading } = useBranding();
  // `resolveAppDisplayName`, not `brand_name`: every new tenant inherits the
  // platform default brand_name ("GetOwnApp Commerce", settings.py DEFAULTS)
  // until an admin edits Branding, whereas `mobile_app_name` falls back to the
  // tenant's own `name` server-side and is therefore always tenant-specific.
  const brandName = resolveAppDisplayName(branding);

  const f = branding?.home_featured || {};
  // Image precedence: what the admin configured -> the tenant's own logo
  // (the same source StoreHeader uses) -> nothing at all.
  const configuredImg = (f.image_url || "").trim();
  const brandImg = resolveBrandImage(branding);
  const featuredImg = configuredImg || brandImg;
  // A logo is artwork on a transparent/solid ground, not a photograph — it
  // must be contained, not cropped to fill, or it renders distorted.
  const imgIsLogo = !configuredImg && !!brandImg;
  // Copy defaults stay business-neutral and brand-derived. The previous
  // ethnic-wear strings were preset marketing, wrong for most tenants.
  const hasCopy = !!(f.overline || f.title || f.body);
  const featured = {
    overline: f.overline ?? "",
    title: f.title ?? `Discover ${brandName}`,
    body: f.body ?? "",
    cta_label: f.cta_label ?? "Shop Now",
    cta_link: f.cta_link || "/shop",
    image_url: featuredImg,
  };
  // Nothing configured and no logo to show — render nothing rather than a
  // hollow placeholder section.
  const showFeatured = hasCopy || !!featuredImg;

  const [categories, setCategories] = useState([]);
  const [categorySections, setCategorySections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: cats } = await api.get("/categories");
        if (!alive) return;
        setCategories(cats || []);

        // Reference (asfashion.store) renders one product section PER
        // top-level category ("Kurta Sets", "Co-Ord Sets", "Palazzo Sets",
        // "Anarkali Suits") — up to MAX_HOME_SECTIONS. Categories with
        // zero active products are skipped so the page never renders
        // empty section headers.
        //
        // We check a larger CANDIDATE_POOL of top-level categories for
        // products BEFORE capping to MAX_HOME_SECTIONS — capping first
        // (on raw `order` position) would let an early category with
        // zero products waste one of the 5 home-page slots while a
        // later category that actually has stock (e.g. "Cosmetics"
        // ordered 6th+) never even gets queried and silently never
        // appears on the home page.
        const MAX_HOME_SECTIONS = 5;
        const CANDIDATE_POOL = 12;
        const topCats = (cats || [])
          .filter((c) => !c.parent_id && c.is_active !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .slice(0, CANDIDATE_POOL);

        const results = await Promise.all(
          topCats.map(async (cat) => {
            try {
              const { data } = await api.get("/products", {
                params: { category: cat.slug, limit: 4 },
              });
              return {
                category: cat,
                products: data?.items || [],
              };
            } catch {
              return { category: cat, products: [] };
            }
          }),
        );
        if (!alive) return;
        setCategorySections(
          results.filter((r) => r.products.length > 0).slice(0, MAX_HOME_SECTIONS),
        );
      } catch (e) {
        if (alive)
          setError(
            e?.response?.data?.detail || "Could not load homepage content.",
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div data-testid="asfashion-home" data-theme-preset="asfashion">
      <AsFashionHero branding={branding} />
      <AsFashionCategoryGrid categories={categories} />

      {error && (
        <div
          className="mx-auto max-w-4xl px-4 py-4 text-sm text-red-700 bg-red-50 border border-red-200 my-6"
          data-testid="asf-home-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="mx-auto w-full max-w-7xl px-4 py-16">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] animate-pulse"
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          {categorySections.map(({ category, products }) => (
            <ProductSection
              key={category.id}
              title={category.name}
              subtitle={
                category.description ||
                `Discover our curated ${category.name} collection.`
              }
              products={products}
              viewAllTo={`/shop/${category.slug}`}
              testid={`asf-section-${category.slug}`}
            />
          ))}

          {/* Reference order: all product sections → Featured callout →
             Trust badges. Callout is always rendered; when there are
             zero product sections it becomes the first content block
             after the category chips, which is acceptable for empty
             tenants (they don't have merchandising yet). */}
          {showFeatured && (
            <FeaturedCallout featured={featured} imgIsLogo={imgIsLogo} />
          )}

          {/* Wait for this store's settings: drawing the defaults first
             would flash a section the tenant has switched off. */}
          {!brandingLoading && (
            <TrustBadges badges={branding?.home_trust_badges} />
          )}
        </>
      )}
    </div>
  );
}
